/**
 * Frontend Role Utilities
 * Centralized role detection and checking functions for frontend components
 */

const normalizeRoleId = (role) => {
  if (!role) return "";
  return String(role?._id || role);
};

export const getDefaultRole = (user) => {
  if (!Array.isArray(user?.roles) || user.roles.length === 0) return null;

  const linkedRoleIds = new Set(
    Array.isArray(user?.linkedRoleIds)
      ? user.linkedRoleIds.map((role) => normalizeRoleId(role))
      : []
  );

  const defaultRole = user.roles.find((role) => {
    const roleId = normalizeRoleId(role);
    return roleId && !linkedRoleIds.has(roleId);
  });

  return defaultRole || user.roles[0] || null;
};

export const getActiveRole = (user) => {
  if (!user) return null;

  if (user.activeRoleId?.roleName) {
    return user.activeRoleId;
  }

  if (user.activeRoleId && Array.isArray(user.roles)) {
    const activeRoleId = normalizeRoleId(user.activeRoleId);
    const activeRole = user.roles.find((role) => {
      return normalizeRoleId(role) === activeRoleId;
    });

    if (activeRole) {
      return activeRole;
    }

    // If the stored active role cannot be resolved, fail closed to the
    // user's default role instead of widening access to every linked role.
    return getDefaultRole(user);
  }

  if (!user.activeRoleId) {
    return getDefaultRole(user);
  }

  return null;
};

/**
 * Get the effective role name for a user (with activeRoleId support)
 * @param {Object} user - User object from localStorage or API
 * @returns {string} The effective role name
 */
export const getEffectiveRoleName = (user) => {
  if (!user) return '';

  const activeRole = getActiveRole(user);
  if (activeRole?.roleName) {
    return activeRole.roleName;
  }

  // Final fallback: user.role (LEGACY users)
  return user.role || '';
};

/**
 * Check if user has a specific role
 * @param {Object} user - User object from localStorage or API
 * @param {string} roleToCheck - Role name to check for
 * @returns {boolean} True if user has the role
 */
export const hasRole = (user, roleToCheck) => {
  const effectiveRole = getEffectiveRoleName(user);
  return effectiveRole.toLowerCase() === roleToCheck.toLowerCase();
};

/**
 * Check if user is an employee
 * @param {Object} user - User object from localStorage or API
 * @returns {boolean} True if user is an employee
 */
export const isEmployee = (user) => {
  return hasRole(user, 'employee');
};

/**
 * Check if user's active role is employee (no fallback to other roles)
 * @param {Object} user - User object from localStorage or API
 * @returns {boolean} True if active role is employee
 */
export const isActiveRoleEmployee = (user) => {
  if (!user) return false;

  if (user.activeRoleId?.roleName) {
    return String(user.activeRoleId.roleName).toLowerCase() === "employee";
  }

  if (user.activeRoleId && Array.isArray(user.roles)) {
    const activeRoleId = user.activeRoleId._id || user.activeRoleId;
    const activeRole = user.roles.find((role) => {
      const roleId = role._id || role;
      return roleId && roleId.toString() === activeRoleId.toString();
    });
    return String(activeRole?.roleName || "").toLowerCase() === "employee";
  }

  return false;
};

/**
 * Check if user is a super admin
 * @param {Object} user - User object from localStorage or API
 * @returns {boolean} True if user is a super admin
 */
export const isSuperAdmin = (user) => {
  const effectiveRole = getEffectiveRoleName(user);
  const userRole = user?.role || '';

  const superAdminRoles = ['superadmin', 'super admin', 'admin'];

  return (
    (effectiveRole && superAdminRoles.includes(effectiveRole.toLowerCase())) ||
    (userRole && superAdminRoles.includes(userRole.toLowerCase()))
  );
};

/**
 * Check if user is a recruiter
 * @param {Object} user - User object from localStorage or API
 * @returns {boolean} True if user is a recruiter
 */
export const isRecruiter = (user) => {
  return hasRole(user, 'recruiter');
};

/**
 * Check if user is a hiring manager
 * @param {Object} user - User object from localStorage or API
 * @returns {boolean} True if user is a hiring manager
 */
export const isHiringManager = (user) => {
  return hasRole(user, 'hiringManager');
};

/**
 * Get logged in user from localStorage
 * @returns {Object} User object or empty object if not found
 */
export const getLoggedInUser = () => {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : {};
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return {};
  }
};
